// calculators/minimum-payment/src/calculator.ts
// UI logic for the public Minimum Payment Trap Calculator.
// Shows how long minimum payments really take + how much an extra monthly
// amount saves in time and interest. Renders a 1- or 2-series chart.

import {
  simulateMinimumPayoff,
  simulatePayoff,
} from "../../../../shared/payoffSimulation";
import {
  renderMultiSeriesChart,
  seriesFromMonths,
  clearChart,
  type ChartSeries,
} from "../../_shared/multiSeriesChart";

const CALC_ID = "minimum_payment";
const CHART_ID = "tc-chart";
const CHART_CAP_MONTHS = 360; // cap minimums-only series for the visual

type FieldName = "balance" | "apr" | "extra";

interface Inputs {
  balance: number;
  apr: number; // decimal
  extra: number; // dollars, may be 0
}

interface ValidationResult {
  valid: boolean;
  values?: Inputs;
  errors: Partial<Record<FieldName, string>>;
}

const DEBOUNCE_MS = 300;
const GA_INPUT_DEBOUNCE_MS = 1000;
const MAX_AMOUNT = 100000;
const MAX_APR_PCT = 50;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function t(
  key: string,
  fallback: string,
  params?: Record<string, string | number>
): string {
  const w = window as unknown as {
    i18next?: { t: (k: string, p?: Record<string, string | number>) => string };
  };
  if (w.i18next && typeof w.i18next.t === "function") {
    const val = w.i18next.t(key, { defaultValue: fallback, ...(params || {}) });
    return typeof val === "string" ? val : fallback;
  }
  if (params) {
    return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      params[k] === undefined ? `{{${k}}}` : String(params[k])
    );
  }
  return fallback;
}

function gtag(...args: unknown[]): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === "function") w.gtag(...args);
}

// Current site locale — same source/priority as js/i18n.js and store-tracking.js
// (i18next active language → <html lang> → ?lang= → 'en'). Sent on every event.
function getLang(): string {
  const SUPPORTED = ["en", "es", "fr", "en-GB", "en-AU", "en-CA", "fr-CA"];
  try {
    const w = window as unknown as {
      i18next?: { language?: string };
      getSiteLang?: () => string;
    };
    if (typeof w.getSiteLang === "function") return w.getSiteLang();
    if (w.i18next && typeof w.i18next.language === "string" && w.i18next.language) {
      return w.i18next.language;
    }
    const docLang = document.documentElement.getAttribute("lang");
    if (docLang && SUPPORTED.indexOf(docLang) !== -1) return docLang;
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && SUPPORTED.indexOf(param) !== -1) return param;
  } catch {
    /* fall through */
  }
  return "en";
}

function formatCurrency(n: number): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s,$]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parsePct(raw: string): number | null {
  const cleaned = raw.replace(/[\s,%]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n / 100;
}

function readInputs(): Record<FieldName, string> {
  return {
    balance: ($("tc-input-balance") as HTMLInputElement | null)?.value || "",
    apr: ($("tc-input-apr") as HTMLInputElement | null)?.value || "",
    extra: ($("tc-input-extra") as HTMLInputElement | null)?.value || "",
  };
}

function validate(raw: Record<FieldName, string>): ValidationResult {
  const errors: Partial<Record<FieldName, string>> = {};

  const balance = parseAmount(raw.balance);
  if (balance === null || balance <= 0) {
    errors.balance = t(
      "calculators.minPayment.error_balance_required",
      "Enter a balance greater than 0."
    );
  } else if (balance > MAX_AMOUNT) {
    errors.balance = t(
      "calculators.minPayment.error_balance_too_large",
      "Balance must be $100,000 or less."
    );
  }

  const apr = parsePct(raw.apr);
  if (apr === null || apr <= 0) {
    errors.apr = t(
      "calculators.minPayment.error_apr_required",
      "Enter an APR greater than 0."
    );
  } else if (apr * 100 > MAX_APR_PCT) {
    errors.apr = t(
      "calculators.minPayment.error_apr_too_large",
      "APR must be 50% or less."
    );
  }

  // Extra is optional; blank → 0.
  let extra = parseAmount(raw.extra);
  if (extra === null) extra = 0;
  if (extra < 0) {
    errors.extra = t(
      "calculators.minPayment.error_extra_invalid",
      "Extra payment can't be negative."
    );
  }

  if (
    Object.keys(errors).length === 0 &&
    balance !== null &&
    apr !== null &&
    extra !== null
  ) {
    return { valid: true, values: { balance, apr, extra }, errors };
  }
  return { valid: false, errors };
}

function renderFieldError(field: FieldName, message: string | null): void {
  const errEl = $(`tc-error-${field}`);
  const inputEl = $(`tc-input-${field}`);
  if (errEl) {
    errEl.textContent = message || "";
    errEl.style.display = message ? "" : "none";
  }
  if (inputEl) {
    if (message) inputEl.setAttribute("aria-invalid", "true");
    else inputEl.removeAttribute("aria-invalid");
  }
}

function showResults(): void {
  const r = $("tc-results");
  if (r) r.style.display = "block";
}

function hideResults(): void {
  const r = $("tc-results");
  if (r) r.style.display = "none";
  clearChart(CHART_ID);
}

interface ComputeResult {
  minMonths: number | null;
  minInterest: number;
  minNeverPaysOff: boolean;
  hasExtra: boolean;
  extraMonths: number | null;
  extraInterest: number;
  extraMonthly: number;
  monthsSaved: number;
  interestSaved: number;
  series: ChartSeries[];
}

function compute(v: Inputs): ComputeResult {
  const { balance, apr, extra } = v;

  // Minimums-only: shrinking minimum payments.
  const minSim = simulateMinimumPayoff({ balance, apr, months: 1200 });
  const minNeverPaysOff = minSim.monthsToPayoff === null;

  const hasExtra = extra > 0;

  // With extra: fixed payment = first month's minimum + extra.
  const baseMin = Math.max(25, balance * 0.02);
  const extraMonthly = baseMin + extra;
  let extraMonths: number | null = null;
  let extraInterest = 0;
  let extraSim: ReturnType<typeof simulatePayoff> | null = null;
  if (hasExtra) {
    extraSim = simulatePayoff({
      balance,
      apr,
      monthlyPayment: extraMonthly,
      months: 1200,
      stopOnPaidOff: true,
    });
    extraMonths = extraSim.monthsToPayoff;
    extraInterest = extraSim.totalInterest;
  }

  const monthsSaved =
    hasExtra && minSim.monthsToPayoff !== null && extraMonths !== null
      ? Math.max(0, minSim.monthsToPayoff - extraMonths)
      : 0;
  const interestSaved = hasExtra
    ? Math.max(0, minSim.totalInterest - extraInterest)
    : 0;

  // ── Chart series ─────────────────────────────────────────────────────────
  const minCap = Math.min(
    minNeverPaysOff ? CHART_CAP_MONTHS : minSim.months.length,
    minSim.months.length
  );
  const minMonthsArr = minSim.months.slice(0, minCap);

  const series: ChartSeries[] = [
    {
      label: t("calculators.minPayment.series_minimums", "Minimum payments"),
      color: hasExtra ? "#94A3B8" : "#00876B",
      points: seriesFromMonths(minMonthsArr),
      stillOwing: minNeverPaysOff,
    },
  ];
  if (hasExtra && extraSim) {
    const extraNeverPaysOff = extraMonths === null;
    const extraCap = Math.min(
      extraNeverPaysOff ? CHART_CAP_MONTHS : extraSim.months.length,
      extraSim.months.length
    );
    const extraMonthsArr = extraSim.months.slice(0, extraCap);
    series.push({
      label: t("calculators.minPayment.series_extra", "With extra payment"),
      color: "#00876B",
      points: seriesFromMonths(extraMonthsArr),
      stillOwing: extraNeverPaysOff,
    });
  }

  return {
    minMonths: minSim.monthsToPayoff,
    minInterest: minSim.totalInterest,
    minNeverPaysOff,
    hasExtra,
    extraMonths,
    extraInterest,
    extraMonthly,
    monthsSaved,
    interestSaved,
    series,
  };
}

function fmtDuration(months: number | null): string {
  if (months === null) return t("calculators.minPayment.never", "never (60+ yrs)");
  if (months < 12) return `${months} ${t("calculators.minPayment.months", "months")}`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yrLabel = t("calculators.minPayment.years", "yrs");
  const moLabel = t("calculators.minPayment.months_short", "mo");
  return rem === 0 ? `${years} ${yrLabel}` : `${years} ${yrLabel} ${rem} ${moLabel}`;
}

function renderResults(v: Inputs, r: ComputeResult): void {
  const heroEl = $("tc-result-hero");
  if (heroEl) {
    heroEl.classList.toggle("is-negative", r.minNeverPaysOff);
    if (r.minNeverPaysOff) {
      heroEl.innerHTML = t(
        "calculators.minPayment.result_never",
        "On minimums alone, this balance <strong>never gets paid off</strong>"
      );
    } else {
      heroEl.innerHTML = t(
        "calculators.minPayment.result_takes",
        "On minimums alone, this takes <strong>{{duration}}</strong>",
        { duration: fmtDuration(r.minMonths) }
      );
    }
  }

  const minTimeEl = $("tc-result-min-time");
  if (minTimeEl) {
    minTimeEl.innerHTML =
      t("calculators.minPayment.metric_min_time", "Minimums payoff time") +
      `: <strong>${r.minNeverPaysOff ? "∞" : fmtDuration(r.minMonths)}</strong>`;
  }

  const minIntEl = $("tc-result-min-interest");
  if (minIntEl) {
    minIntEl.innerHTML =
      t("calculators.minPayment.metric_min_interest", "Interest on minimums") +
      `: <strong>${r.minNeverPaysOff ? "∞" : formatCurrency(r.minInterest)}</strong>`;
  }

  const savedEl = $("tc-result-saved");
  if (savedEl) {
    if (r.hasExtra && r.minNeverPaysOff) {
      // The capped sim's interestSaved/monthsSaved are meaningless here, so
      // surface a qualitative message instead of a fabricated dollar figure.
      savedEl.innerHTML = t(
        "calculators.minPayment.neverPaysOffWarning",
        "Minimums never clear this balance"
      );
    } else if (r.hasExtra) {
      savedEl.innerHTML =
        t("calculators.minPayment.metric_saved", "Saved with extra") +
        `: <strong>${formatCurrency(r.interestSaved)}</strong>`;
    } else {
      savedEl.innerHTML =
        t("calculators.minPayment.metric_try_extra", "Try an extra $25–$100/mo") +
        " →";
    }
  }

  // Summary line when extra payment is set.
  const summaryEl = $("tc-result-summary");
  if (summaryEl) {
    if (r.hasExtra && r.minNeverPaysOff) {
      summaryEl.style.display = "block";
      if (r.extraMonths === null) {
        // The extra payment also never clears the balance, so there is no
        // duration/interest to report. Show a qualitative warning only and
        // avoid re-introducing the capped figure.
        summaryEl.innerHTML = t(
          "calculators.minPayment.neverPaysOffBothWarning",
          "Even with an extra {{extra}}/mo, minimums on this balance may never fully pay it off. Try a larger payment.",
          {
            extra: formatCurrency(v.extra),
          }
        );
      } else {
        // Minimums never clear the balance, so a "saved vs minimums" delta is
        // meaningless. Lead with the qualitative fact, then let the extra-payment
        // result stand on its own (its own months + total interest).
        summaryEl.innerHTML = t(
          "calculators.minPayment.summary_never_pays_off",
          "Minimums never fully pay off this balance — adding {{extra}}/mo clears it in {{duration}} for {{interest}} in interest.",
          {
            extra: formatCurrency(v.extra),
            duration: fmtDuration(r.extraMonths),
            interest: `<strong>${formatCurrency(r.extraInterest)}</strong>`,
          }
        );
      }
    } else if (r.hasExtra) {
      summaryEl.style.display = "block";
      summaryEl.innerHTML = t(
        "calculators.minPayment.summary_with_extra",
        "Paying {{payment}}/mo clears it in {{duration}}, saving {{interest}} in interest and {{months}} months versus minimums.",
        {
          payment: formatCurrency(r.extraMonthly),
          duration: fmtDuration(r.extraMonths),
          interest: `<strong>${formatCurrency(r.interestSaved)}</strong>`,
          months: r.monthsSaved,
        }
      );
    } else {
      summaryEl.style.display = "none";
    }
  }

  const svg = $(CHART_ID) as unknown as SVGSVGElement | null;
  if (svg)
    renderMultiSeriesChart(
      svg,
      r.series,
      t("calculators.minPayment.stillOwing", "still owing")
    );

  gtag("event", "calc_result_shown", {
    calculator: CALC_ID,
    placement: "calculator",
    has_extra: r.hasExtra ? "yes" : "no",
    never_pays_off: r.minNeverPaysOff ? "yes" : "no",
    lang: getLang(),
  });
}

let recalcTimer: number | null = null;
let gaInputTimer: number | null = null;
let lastChangedField: string | null = null;

function run(): void {
  const raw = readInputs();
  const v = validate(raw);

  (["balance", "apr", "extra"] as FieldName[]).forEach((f) =>
    renderFieldError(f, v.errors[f] || null)
  );

  if (!v.valid || !v.values) {
    hideResults();
    return;
  }

  try {
    const result = compute(v.values);
    showResults();
    renderResults(v.values, result);
  } catch (err) {
    const msg =
      err instanceof Error ? err.message.slice(0, 80) : String(err).slice(0, 80);
    hideResults();
    const errBox = $("tc-runtime-error");
    if (errBox) {
      errBox.textContent = t(
        "calculators.minPayment.runtime_error",
        "Something went wrong. Try refreshing the page."
      );
      errBox.style.display = "block";
    }
    gtag("event", "calc_error", { calculator: CALC_ID, placement: "calculator", message: msg, lang: getLang() });
  }
}

function scheduleRecalc(): void {
  if (recalcTimer !== null) window.clearTimeout(recalcTimer);
  recalcTimer = window.setTimeout(() => {
    recalcTimer = null;
    run();
  }, DEBOUNCE_MS);
}

function scheduleGaInputEvent(): void {
  if (gaInputTimer !== null) window.clearTimeout(gaInputTimer);
  gaInputTimer = window.setTimeout(() => {
    gaInputTimer = null;
    if (lastChangedField) {
      gtag("event", "calc_input_change", {
        calculator: CALC_ID,
        placement: "calculator",
        field: lastChangedField,
        lang: getLang(),
      });
    }
  }, GA_INPUT_DEBOUNCE_MS);
}

export function attachCalculator(): void {
  (["balance", "apr", "extra"] as FieldName[]).forEach((field) => {
    const el = $(`tc-input-${field}`) as HTMLInputElement | null;
    if (!el) return;
    el.addEventListener("input", () => {
      lastChangedField = field;
      const errBox = $("tc-runtime-error");
      if (errBox) errBox.style.display = "none";
      scheduleRecalc();
      scheduleGaInputEvent();
    });
  });

  document.querySelectorAll<HTMLAnchorElement>("[data-calc-cta]").forEach((a) => {
    a.addEventListener("click", () => {
      const store = a.getAttribute("data-calc-cta") || "unknown";
      const lang = getLang();
      // Keep calc_cta_click (it has GA history). The unified
      // `app_store_click` event is now owned solely by /js/store-tracking.js
      // (the global delegated listener) to avoid double-counting.
      gtag("event", "calc_cta_click", { calculator: CALC_ID, placement: "calculator", store, lang });
    });
  });

  const calcButton = $("tc-calculate-button") as HTMLButtonElement | null;
  if (calcButton) {
    calcButton.addEventListener("click", (e) => {
      e.preventDefault();
      run();
    });
  }

  const form = $("tc-form") as HTMLFormElement | null;
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      run();
    });
  }

  gtag("event", "calc_view", { calculator: CALC_ID, placement: "calculator", lang: getLang() });

  // Only auto-run once the balance is entered (apr has a default value but
  // balance is placeholder-only), so we don't paint a validation error on load.
  if (readInputs().balance) run();
}
